import { Button } from '@crowdex/ui/components/button';
import { AppWindow } from 'lucide-react';
import { useState } from 'react';
import { ItemIcon } from '@/components/item-icon';
import { useRouter } from '@/lib/navigation';
import { pluginPagePath } from '@/lib/plugins/plugin-sections';

/**
 * An app that needs an account the user has not connected (D29d): one screen saying so, with the
 * action that connects it (the plugin's page, where its connections are), instead of an error.
 */
export function AppConnectScreen({
  plugin,
  title,
  description,
  iconSrc,
}: {
  plugin: string;
  title: string;
  /** What the app does (its `display.description`), shown on "What it does". */
  description?: string | undefined;
  iconSrc?: string | undefined;
}) {
  const router = useRouter();
  const [explained, setExplained] = useState(false);
  const icon = (size: string) => <ItemIcon className={size} icon={AppWindow} src={iconSrc} />;
  return (
    <section aria-label={title} className="flex size-full flex-col bg-background">
      <header className="flex h-14 shrink-0 items-center gap-2.5 px-6">
        <span
          aria-hidden
          className="flex size-7 items-center justify-center rounded-lg bg-control text-muted-foreground"
        >
          {icon('size-3.5')}
        </span>
        <h2 className="font-semibold text-base">{title}</h2>
        <span className="type-meta text-muted-foreground">Not connected yet</span>
      </header>
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 overflow-y-auto p-6 text-center">
        <span
          aria-hidden
          className="flex size-14 items-center justify-center rounded-2xl bg-control text-foreground"
        >
          {icon('size-6')}
        </span>
        <h3 className="type-card-title mt-1">Connect {title}</h3>
        <p className="type-meta max-w-md text-muted-foreground">
          Connect your {title} account once; every conversation can then use it.
        </p>
        {explained && description !== undefined && (
          <p className="type-meta max-w-md text-muted-foreground" data-testid="app-description">
            {description}
          </p>
        )}
        <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
          <Button onClick={() => router.push(pluginPagePath('installed', plugin))} type="button">
            Connect {title}
          </Button>
          {description !== undefined && !explained && (
            <Button onClick={() => setExplained(true)} type="button" variant="outline">
              What it does
            </Button>
          )}
        </div>
      </div>
    </section>
  );
}
